import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { HelpTip } from '../../Experiments/Details/ExperimentSettings/TargetingHelp';
import { FLAG_COPY } from '../copy';

type FlagSectionProps = {
  title: string;
  /** What the card does, in one or two plain sentences, behind the ⓘ beside the title. */
  help: string;
  description?: string;
  /** Beside the title on the right: a status chip, a guide link, the plan's buttons. */
  actions?: ReactNode;
  /** A closing strip for a form's Save. */
  footer?: ReactNode;
  tourAnchor?: string;
  children: ReactNode;
};

/**
 * A card of the flag page, in the experiment settings cards' shape (Variants, Targeting &
 * conditions, Activity log): a micro-label title with its ⓘ, one line on what the card is for,
 * the card's buttons on the right, then the body, and a closing strip for Save when a form has
 * unsaved changes.
 */
export const FlagSection = ({ title, help, description, actions, footer, tourAnchor, children }: FlagSectionProps) => (
  <section className="bg-panel border-line rounded-panel border" data-tour={tourAnchor}>
    <div className="border-line flex flex-col gap-2 border-b px-4 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h2 className="micro-label text-foreground">{title}</h2>
          <HelpTip text={help} label={title.toLowerCase()} />
        </div>
        {description && <p className="supporting mt-1">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
    <div className="p-4">{children}</div>
    {footer && (
      <div className="border-line flex flex-col-reverse gap-2 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-end">
        {footer}
      </div>
    )}
  </section>
);

type FieldLabelProps = { htmlFor?: string; label: string; help?: string };

/** A field's micro-label, with an ⓘ where the field is not obvious from its name. */
export const FieldLabel = ({ htmlFor, label, help }: FieldLabelProps) => (
  <span className="flex items-center gap-1.5">
    {htmlFor ? (
      <label htmlFor={htmlFor} className="micro-label">
        {label}
      </label>
    ) : (
      <span className="micro-label">{label}</span>
    )}
    {help && <HelpTip text={help} label={label.toLowerCase()} />}
  </span>
);

type SaveStripProps = {
  /** Why Save is off, in a sentence. Without one the strip says the changes are not saved yet. */
  blockingReason?: string;
  saveLabel: string;
  discardLabel: string;
  onSave: () => void;
  onDiscard: () => void;
  isSaving: boolean;
  /** False where the card already marks what is not saved (the Rollout card's figure). */
  marksUnsaved?: boolean;
};

/**
 * The footer a card shows while its form has unsaved changes: why it cannot save, or that it is
 * not saved yet in the console's unsaved style, then Discard and Save.
 */
export const SaveStrip = (props: SaveStripProps) => {
  const { blockingReason, saveLabel, discardLabel, onSave, onDiscard, isSaving, marksUnsaved = true } = props;
  return (
    <>
      {blockingReason ? (
        <span className="supporting sm:mr-auto">{blockingReason}</span>
      ) : marksUnsaved ? (
        <span className="micro-label text-warn sm:mr-auto">{FLAG_COPY.common.unsaved}</span>
      ) : null}
      <Button type="button" variant="outline" onClick={onDiscard} className={SECONDARY_BUTTON_CLASS}>
        {discardLabel}
      </Button>
      <Button
        type="button"
        onClick={onSave}
        disabled={!!blockingReason || isSaving}
        className="bg-cta hover:bg-ctahover h-8 rounded-lg px-3 text-meta text-white"
      >
        {saveLabel}
      </Button>
    </>
  );
};

/** The secondary button the experiment cards use (VariantsConfig). */
export const SECONDARY_BUTTON_CLASS =
  'border-line text-foreground hover:bg-hover h-8 rounded-lg border bg-transparent px-3 text-meta';

/** The console's field. */
export const FIELD_CLASS = 'border-input bg-field text-foreground h-9 rounded-lg text-body';
